import '@repo/editor/themes/camp-input';
import '@repo/editor/themes/camp-table';
import '@repo/editor/themes/playground';
import '@repo/editor/themes/responsive-tables';

import * as Sentry from '@sentry/react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router';
import { toast, Toaster } from 'sonner';

import ErrorBoundary from './components/error-boundary';
import { ConfirmDialog } from './components/ui/confirm-dialog';
import { router } from './routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  // react-query v5는 useQuery 단위 onError가 없으므로, 모든 쿼리 실패를
  // 전역에서 Sentry로 보고한다. (queryKey에 assignmentId 등 식별자가 들어있음)
  queryCache: new QueryCache({
    onError: (error, query) => {
      Sentry.withScope((scope) => {
        scope.setTag('errorSource', 'query');
        scope.setContext('query', { queryKey: query.queryKey });
        Sentry.captureException(error);
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: string }).message)
          : '처리하는데 실패했습니다.';
      toast.error(message);
    },
  }),
});

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>
            <RouterProvider router={router} />
            <Toaster position="top-right" richColors />
            <ConfirmDialog />
          </NuqsAdapter>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
